import React, { useMemo, useState } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Grid,
    Chip,
    Alert,
    MenuItem,
} from "@mui/material";
import { Cancel, Edit, Save } from "@mui/icons-material";
import { WB_SX, WB_UI } from "../../../ui/ux/uiTokens";

const VERTICAL_OPTIONS = [
    "UNDEFINED",
    "OTHER",
    "AUTO",
    "BEAUTY",
    "APPAREL",
    "EDU",
    "ENTERTAIN",
    "EVENT_PLAN",
    "FINANCE",
    "GROCERY",
    "GOVT",
    "HOTEL",
    "HEALTH",
    "NONPROFIT",
    "PROF_SERVICES",
    "RETAIL",
    "TRAVEL",
    "RESTAURANT",
    "ALCOHOL",
];

const InfoRow = ({ label, value }) => {
    if (value == null || value === "") return null;
    return (
        <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">
                {label}
            </Typography>
            <Typography variant="body1" fontWeight={600} sx={{ whiteSpace: "pre-wrap" }}>
                {value}
            </Typography>
        </Grid>
    );
};

/**
 * Public WhatsApp Business Profile editor (Meta profile fields).
 * Uses POST .../business-profile for combined field updates.
 */
const BusinessProfilePanel = ({
    profile,
    phoneNumbers = [],
    selectedPhoneId,
    onPhoneChange,
    onSave,
    loading = false,
}) => {
    const [editMode, setEditMode] = useState(false);
    const initialForm = useMemo(
        () => ({
            about: profile?.about || "",
            email: profile?.email || "",
            address: profile?.address || "",
            description: profile?.description || "",
            websites: (profile?.websites || []).join(", "),
            vertical: profile?.vertical || "",
        }),
        [profile]
    );
    const [form, setForm] = useState(initialForm);

    React.useEffect(() => {
        if (!editMode) {
            setForm(initialForm);
        }
    }, [initialForm, editMode]);

    const handleCancel = () => {
        setForm(initialForm);
        setEditMode(false);
    };

    const handleSave = async () => {
        const websites = form.websites
            .split(",")
            .map((w) => w.trim())
            .filter(Boolean)
            .slice(0, 2);

        const payload = {
            about: form.about.trim() || undefined,
            email: form.email.trim() || undefined,
            address: form.address.trim() || undefined,
            description: form.description.trim() || undefined,
            websites: websites.length ? websites : undefined,
            vertical: form.vertical.trim() || undefined,
        };

        const ok = await onSave?.(payload);
        if (ok !== false) {
            setEditMode(false);
        }
    };

    return (
        <Box>
            {phoneNumbers.length > 0 && (
                <TextField
                    select
                    label="Phone Number"
                    size="small"
                    value={selectedPhoneId || ""}
                    onChange={(e) => onPhoneChange?.(e.target.value)}
                    sx={{ mb: 2, minWidth: 280 }}
                    helperText="Profile is managed per phone number"
                >
                    {phoneNumbers.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                            {p.display_phone_number || p.verified_name || p.id}
                        </MenuItem>
                    ))}
                </TextField>
            )}

            {!profile && !editMode ? (
                <Alert severity="info">
                    No business profile loaded yet. Select a phone number or refresh.
                </Alert>
            ) : (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                            flexWrap: "wrap",
                            gap: 1,
                        }}
                    >
                        <Typography variant="subtitle2" sx={WB_SX.sectionTitle}>
                            Public WhatsApp Profile
                        </Typography>
                        {!editMode ? (
                            <Button
                                variant="contained"
                                startIcon={<Edit />}
                                onClick={() => setEditMode(true)}
                                sx={WB_SX.primaryButton}
                                disabled={loading}
                            >
                                Edit Profile
                            </Button>
                        ) : (
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<Cancel />}
                                    onClick={handleCancel}
                                    sx={WB_SX.secondaryButton}
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<Save />}
                                    onClick={handleSave}
                                    sx={WB_SX.primaryButton}
                                    disabled={loading}
                                >
                                    Save Profile
                                </Button>
                            </Box>
                        )}
                    </Box>

                    {profile?.profile_picture_url && (
                        <Box
                            component="img"
                            src={profile.profile_picture_url}
                            alt="Profile"
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: 2,
                                objectFit: "cover",
                                mb: 2,
                                border: `1px solid ${WB_UI.color.border}`,
                            }}
                        />
                    )}

                    {editMode ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="About"
                                    value={form.about}
                                    onChange={(e) => setForm({ ...form, about: e.target.value })}
                                    multiline
                                    rows={2}
                                    helperText="1–139 characters"
                                    inputProps={{ maxLength: 139 }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    multiline
                                    rows={3}
                                    helperText={`${form.description.length}/512`}
                                    inputProps={{ maxLength: 512 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    inputProps={{ maxLength: 128 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Vertical / Industry"
                                    value={form.vertical}
                                    onChange={(e) => setForm({ ...form, vertical: e.target.value })}
                                >
                                    <MenuItem value="">
                                        <em>Not set</em>
                                    </MenuItem>
                                    {VERTICAL_OPTIONS.map((v) => (
                                        <MenuItem key={v} value={v}>
                                            {v}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Websites (comma-separated, max 2)"
                                    value={form.websites}
                                    onChange={(e) => setForm({ ...form, websites: e.target.value })}
                                    placeholder="https://example.com, https://shop.example.com"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Address"
                                    value={form.address}
                                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                                    multiline
                                    rows={2}
                                    inputProps={{ maxLength: 256 }}
                                />
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid container spacing={2}>
                            <InfoRow label="About" value={profile?.about} />
                            <InfoRow label="Description" value={profile?.description} />
                            <InfoRow label="Email" value={profile?.email} />
                            <InfoRow label="Address" value={profile?.address} />
                            <InfoRow label="Vertical" value={profile?.vertical} />
                            {profile?.websites?.length > 0 && (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">
                                        Websites
                                    </Typography>
                                    <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                        {profile.websites.map((site) => (
                                            <Chip key={site} label={site} size="small" variant="outlined" />
                                        ))}
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </>
            )}
        </Box>
    );
};

export default BusinessProfilePanel;
